import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from 'reactstrap';
import { Download, X } from 'react-feather';
import { toast } from 'react-toastify';
import {
  getReportsForAdmin,
  getReportsForAdminRange,
  getReportsForTeacherLeaderForAdmin,
  getTeacherMetricPastMonthsData,
  getTeachers,
  teacherProfile,
} from '../../../../Api/apiUtils';
import { getTeacherLeaderUserId, isTeacherLeader } from '../../../../utils/permissions';
import {
  Badge,
  BadgeTone,
  Button,
  Card,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  Select,
  Table,
  TextInput,
  Toolbar,
} from '../../../../ui';
import StatTile from '../SlotAnalysis/ui-pending/StatTile';
import './adminTeacherReports.scss';

type MetricCell = {
  value: string | number | null;
  colour?: string;
  heading?: string;
  bucket?: string | null;
};

type ReportRow = {
  teacher_name?: string;
  teacher_id?: number;
  current_working_status?: string | null;
  payout_category?: string | null;
  course_category?: string | null;
  employment_status?: string | null;
  teacher_join_date?: string | null;
  highest_level_taught?: string | null;
  teacher_leader_name?: string | null;
  [key: string]: any;
};

type AlertsFilter =
  | ''
  | 'qa_pending_ld'
  | 'qa_pending_academics'
  | 'joined_late'
  | 'd14_low'
  | 'has_refunds'
  | 'pip_active'
  | 'has_valid_escalations'
  | 'on_track';

const ALERT_FILTER_OPTIONS: Array<{ value: AlertsFilter; label: string; icon: string }> = [
  { value: '', label: 'No alert filter', icon: '✓' },
  { value: 'qa_pending_ld', label: 'QA Pending - L&D', icon: '📋' },
  { value: 'qa_pending_academics', label: 'QA Pending - Academics', icon: '📝' },
  { value: 'joined_late', label: 'Joined Late', icon: '⏰' },
  { value: 'd14_low', label: 'D14 Engaged < 60%', icon: '📉' },
  { value: 'has_refunds', label: 'Has Refunds', icon: '↩️' },
  { value: 'pip_active', label: 'PIP Active', icon: '⚠️' },
  { value: 'has_valid_escalations', label: 'Has Valid Escalations', icon: '🚨' },
  { value: 'on_track', label: 'On Track', icon: '✅' },
];

const isMetricCell = (value: unknown): value is MetricCell =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value) && 'value' in value);

const getMetricKeys = (rows: ReportRow[]): string[] => {
  if (!rows.length) return [];
  const keys = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (isMetricCell(row[key])) keys.add(key);
    });
  });
  return Array.from(keys);
};

const normalizeHeading = (heading?: string | null): string =>
  String(heading ?? '')
    .toLowerCase()
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getMetricValue = (row: ReportRow, key: string): number | null => {
  const metric = row[key] as MetricCell | undefined;
  if (!metric || metric.value === null || metric.value === undefined || metric.value === '') {
    return null;
  }
  if (typeof metric.value === 'number' && !Number.isNaN(metric.value)) return metric.value;
  const parsed = Number(String(metric.value).replace(/,/g, '').replace(/%/g, '').trim());
  return Number.isNaN(parsed) ? null : parsed;
};

const getMetricRawText = (row: ReportRow, key: string | null): string => {
  if (!key) return '';
  const metric = row[key] as MetricCell | undefined;
  if (!metric || metric.value === null || metric.value === undefined) return '';
  return String(metric.value).trim();
};

const isYesValue = (value: string): boolean => {
  const normalized = value.toLowerCase();
  return normalized === 'yes' || normalized === 'y' || normalized === 'true' || normalized === '1';
};

const findMetricKey = (rows: ReportRow[], patterns: RegExp[]): string | null => {
  const keys = getMetricKeys(rows);
  for (const pattern of patterns) {
    const match = keys.find((key) => pattern.test(key));
    if (match) return match;
  }
  return null;
};

/** Prefer exact key match, then heading text match. */
const findMetricKeyByKeyOrHeading = (
  rows: ReportRow[],
  keyPatterns: RegExp[],
  headingPatterns: RegExp[]
): string | null => {
  const byKey = findMetricKey(rows, keyPatterns);
  if (byKey) return byKey;

  const keys = getMetricKeys(rows);
  for (const pattern of headingPatterns) {
    const match = keys.find((key) => {
      const sample = rows.find((row) => row[key] !== undefined)?.[key] as MetricCell | undefined;
      return pattern.test(normalizeHeading(sample?.heading));
    });
    if (match) return match;
  }
  return null;
};

/** Resolve the exact "D14 Engaged %" column used in the table. */
const findD14EngagedPercentKey = (rows: ReportRow[]): string | null => {
  const keys = getMetricKeys(rows);

  const withHeadings = keys.map((key) => {
    const sample = rows.find((row) => row[key] !== undefined)?.[key] as MetricCell | undefined;
    const heading = normalizeHeading(sample?.heading)
      .replace(/\s*%\s*$/g, '%')
      .replace(/\s+/g, ' ');
    return { key, heading };
  });

  const exactPct =
    withHeadings.find((item) => item.heading === 'd14 engaged%') ||
    withHeadings.find((item) => item.heading === 'd14 engaged %');
  if (exactPct) return exactPct.key;

  const includesPct = withHeadings.find(
    (item) => item.heading.includes('d14') && item.heading.includes('engaged') && item.heading.includes('%')
  );
  if (includesPct) return includesPct.key;

  return findMetricKey(rows, [
    /^d14_engaged_?%$/i,
    /^d14_engaged_pct$/i,
    /^d14_engaged_percent$/i,
    /^d14_engaged$/i,
  ]);
};

const countD14Below60 = (rows: ReportRow[], d14Key: string | null): number => {
  if (!d14Key) return 0;
  const values = rows
    .map((row) => getMetricValue(row, d14Key))
    .filter((value): value is number => value !== null);
  if (!values.length) return 0;

  // Detect 0–1 ratios only when every value is within that range
  const treatAsRatio = Math.max(...values) <= 1;
  return values.filter((value) => (treatAsRatio ? value * 100 : value) < 60).length;
};

/** Average metric values, ignoring null/empty and exact 0 (pending / not scored). */
const averageMetric = (rows: ReportRow[], key: string | null): number | null => {
  if (!key) return null;
  // Ignore null/empty and exact 0 so zeros don't pull averages down.
  const values = rows
    .map((row) => getMetricValue(row, key))
    .filter((value): value is number => value !== null && value !== 0);
  if (!values.length) return null;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
};

const normalizeD14Percent = (value: number, treatAsRatio: boolean): number =>
  treatAsRatio ? value * 100 : value;

const isD14ScaleRatio = (rows: ReportRow[], d14Key: string | null): boolean => {
  if (!d14Key) return false;
  const values = rows
    .map((row) => getMetricValue(row, d14Key))
    .filter((value): value is number => value !== null);
  if (!values.length) return false;
  return Math.max(...values) <= 1;
};

/** QA Pending: component score is exactly 0. */
const isQaComponentPending = (row: ReportRow, key: string | null): boolean => {
  if (!key) return false;
  const value = getMetricValue(row, key);
  return value === 0;
};

const isJoinedLateAlert = (row: ReportRow, key: string | null): boolean => {
  if (!key) return false;
  const value = getMetricValue(row, key);
  return value !== null && value > 0;
};

const isD14LowRow = (
  row: ReportRow,
  d14Key: string | null,
  treatAsRatio = false
): boolean => {
  if (!d14Key) return false;
  const value = getMetricValue(row, d14Key);
  if (value === null) return false;
  return normalizeD14Percent(value, treatAsRatio) < 60;
};

const isHasRefundsAlert = (row: ReportRow, key: string | null): boolean => {
  if (!key) return false;
  const value = getMetricValue(row, key);
  return value !== null && value > 0;
};

const isPipActiveAlert = (row: ReportRow, key: string | null): boolean => {
  if (!key) return false;
  return isYesValue(getMetricRawText(row, key));
};

const isHasValidEscalationsAlert = (row: ReportRow, key: string | null): boolean => {
  if (!key) return false;
  const value = getMetricValue(row, key);
  return value !== null && value > 0;
};

type AlertMetricKeys = {
  qaLd: string | null;
  qaAcademic: string | null;
  d14: string | null;
  joinedLate: string | null;
  refunds: string | null;
  pipActive: string | null;
  escalations: string | null;
};

/** True when any of the 7 alert conditions fires (excludes On Track). */
const hasAnyAlert = (
  row: ReportRow,
  keys: AlertMetricKeys,
  d14AsRatio: boolean
): boolean =>
  isQaComponentPending(row, keys.qaLd) ||
  isQaComponentPending(row, keys.qaAcademic) ||
  isJoinedLateAlert(row, keys.joinedLate) ||
  isD14LowRow(row, keys.d14, d14AsRatio) ||
  isHasRefundsAlert(row, keys.refunds) ||
  isPipActiveAlert(row, keys.pipActive) ||
  isHasValidEscalationsAlert(row, keys.escalations);

const matchesAlertsFilter = (
  row: ReportRow,
  filter: AlertsFilter,
  keys: AlertMetricKeys,
  d14AsRatio: boolean
): boolean => {
  if (!filter) return true;
  switch (filter) {
    case 'qa_pending_ld':
      // QA Score - L&D Component is 0
      return isQaComponentPending(row, keys.qaLd);
    case 'qa_pending_academics':
      // QA Score - Academic Component is 0
      return isQaComponentPending(row, keys.qaAcademic);
    case 'joined_late':
      // Joined Late > 0
      return isJoinedLateAlert(row, keys.joinedLate);
    case 'd14_low':
      // D14 Engaged % < 60
      return isD14LowRow(row, keys.d14, d14AsRatio);
    case 'has_refunds':
      // Refunds > 0
      return isHasRefundsAlert(row, keys.refunds);
    case 'pip_active':
      // PIP Active = Yes
      return isPipActiveAlert(row, keys.pipActive);
    case 'has_valid_escalations':
      // Escalations > 0
      return isHasValidEscalationsAlert(row, keys.escalations);
    case 'on_track':
      // None of the above 7
      return !hasAnyAlert(row, keys, d14AsRatio);
    default:
      return true;
  }
};

const uniqueSorted = (values: Array<string | null | undefined>): string[] =>
  Array.from(
    new Set(
      values
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

const progressWidth = (value: number | null) =>
  value === null ? 0 : Math.max(0, Math.min(100, value));

type SchemaCol =
  | { type: 'teacher'; key: 'teacher_name'; label: string; bucket: null }
  | { type: 'teacher_id'; key: 'teacher_id'; label: string; bucket: null }
  | { type: 'working_status'; key: 'current_working_status'; label: string; bucket: null }
  | { type: 'payout'; key: 'payout_category'; label: string; bucket: null }
  | { type: 'course_cat'; key: 'course_category'; label: string; bucket: null }
  | { type: 'text'; key: 'teacher_join_date' | 'highest_level_taught' | 'teacher_leader_name'; label: string; bucket: null }
  | { type: 'metric'; key: string; label: string; bucket: string | null };

type SortDir = 'asc' | 'desc';

type TeacherMetricTrend = {
  teacher_id: number;
  teacher_name: string;
  parameter: string;
  parameter_direction: 'high' | 'low' | string;
  month: string;
  current_value: number | null;
  past_months_data: Record<string, number | null>;
  six_month_average_value: number | null;
  highest_value: number | null;
  lowest_value: number | null;
};

type TrendContext = {
  heading: string;
  teacherName: string;
  courseCategory: string;
  showPercent: boolean;
};

const trendMonthTimestamp = (key: string): number => {
  const match = key.toLowerCase().match(/^([a-z]{3})[_-](\d{2}|\d{4})$/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  const monthIndex = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(match[1]);
  const year = Number(match[2].length === 2 ? `20${match[2]}` : match[2]);
  return monthIndex < 0 ? Number.MAX_SAFE_INTEGER : new Date(year, monthIndex, 1).getTime();
};

const trendMonthLabel = (key: string): string => {
  const match = key.match(/^([a-z]{3})[_-](\d{2}|\d{4})$/i);
  if (!match) return key;
  return `${match[1].charAt(0).toUpperCase()}${match[1].slice(1).toLowerCase()}`;
};

const formatTrendValue = (value: number | null | undefined, showPercent: boolean): string => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const rounded = Number(Number(value).toFixed(2));
  return `${rounded}${showPercent ? '%' : ''}`;
};

const TrendChart: React.FC<{
  trend: TeacherMetricTrend;
  showPercent: boolean;
}> = ({ trend, showPercent }) => {
  const points = Object.entries(trend.past_months_data || {})
    .filter((entry): entry is [string, number] => Number.isFinite(Number(entry[1])))
    .map(([key, value]) => ({ key, value: Number(value) }))
    .sort((a, b) => trendMonthTimestamp(a.key) - trendMonthTimestamp(b.key));

  if (!points.length) {
    return <EmptyState title="No past-month data available" description="This metric has no history for the last six months." />;
  }

  const width = 600;
  const height = 220;
  const pad = { top: 30, right: 28, bottom: 42, left: 28 };
  const values = points.map((point) => point.value);
  if (Number.isFinite(Number(trend.six_month_average_value))) {
    values.push(Number(trend.six_month_average_value));
  }
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const margin = Math.max((rawMax - rawMin) * 0.25, Math.abs(rawMax) * 0.08, 1);
  const min = rawMin - margin;
  const max = rawMax + margin;
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const xFor = (index: number) =>
    pad.left + (points.length === 1 ? plotWidth / 2 : (index * plotWidth) / (points.length - 1));
  const yFor = (value: number) => pad.top + ((max - value) / (max - min || 1)) * plotHeight;
  const polyline = points.map((point, index) => `${xFor(index)},${yFor(point.value)}`).join(' ');
  const average = Number(trend.six_month_average_value);
  const hasAverage = Number.isFinite(average);

  return (
    <div className="atr-trend-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Six month metric trend">
        {[0, 0.5, 1].map((ratio) => (
          <line
            key={ratio}
            x1={pad.left}
            x2={width - pad.right}
            y1={pad.top + ratio * plotHeight}
            y2={pad.top + ratio * plotHeight}
            className="atr-trend-chart__grid"
          />
        ))}
        {hasAverage ? (
          <line
            x1={pad.left}
            x2={width - pad.right}
            y1={yFor(average)}
            y2={yFor(average)}
            className="atr-trend-chart__average"
          />
        ) : null}
        <polyline points={polyline} className="atr-trend-chart__line" />
        {points.map((point, index) => (
          <g key={point.key}>
            <text x={xFor(index)} y={Math.max(16, yFor(point.value) - 13)} className="atr-trend-chart__value">
              {formatTrendValue(point.value, showPercent)}
            </text>
            <circle cx={xFor(index)} cy={yFor(point.value)} r="7" className="atr-trend-chart__point" />
            <text x={xFor(index)} y={height - 13} className="atr-trend-chart__month">
              {trendMonthLabel(point.key)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const AdminTeacherReports: React.FC = () => {
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const [month, setMonth] = useState<string>(getCurrentMonth());
  const [quarterFilter, setQuarterFilter] = useState<'MONTHLY' | 'Q1' | 'Q2' | 'Q3' | 'Q4'>('MONTHLY');
  const [searchTeacher, setSearchTeacher] = useState<string>('');
  const [courseCategoryFilter, setCourseCategoryFilter] = useState('');
  const [payoutTierFilter, setPayoutTierFilter] = useState('');
  const [workingStatusFilter, setWorkingStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [employmentStatusFilter, setEmploymentStatusFilter] = useState('');
  const [alertsFilter, setAlertsFilter] = useState<AlertsFilter>('');
  const [alertsMenuOpen, setAlertsMenuOpen] = useState(false);
  const alertsMenuRef = useRef<HTMLDivElement | null>(null);
  const alertsPopupRef = useRef<HTMLDivElement | null>(null);
  const [alertsMenuPos, setAlertsMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const [loading, setLoading] = useState<boolean>(false);
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [trendModalOpen, setTrendModalOpen] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendData, setTrendData] = useState<TeacherMetricTrend | null>(null);
  const [trendError, setTrendError] = useState('');
  const [trendContext, setTrendContext] = useState<TrendContext | null>(null);
  const teacherLeaderView = isTeacherLeader();

  /** Menu is portaled to body, so anchor it to the trigger rect (toolbar clips overflow). */
  const syncAlertsMenuPosition = useCallback(() => {
    const rect = alertsMenuRef.current?.getBoundingClientRect();
    if (!rect) return;
    setAlertsMenuPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!alertsMenuOpen) return;
    syncAlertsMenuPosition();

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !alertsMenuRef.current?.contains(target) &&
        !alertsPopupRef.current?.contains(target)
      ) {
        setAlertsMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAlertsMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', syncAlertsMenuPosition);
    window.addEventListener('scroll', syncAlertsMenuPosition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', syncAlertsMenuPosition);
      window.removeEventListener('scroll', syncAlertsMenuPosition, true);
    };
  }, [alertsMenuOpen, syncAlertsMenuPosition]);

  
  const todayMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const normalizeRows = (payload: any): ReportRow[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.reports)) return payload.reports;
    if (Array.isArray(payload?.result)) return payload.result;
    return [];
  };

  const tableSchema = useMemo((): SchemaCol[] => {
    if (!rows.length) return [];
    const keySet = new Set<string>();
    rows.forEach((row) => {
      Object.keys(row).forEach((k) => {
        if (k !== 'teacher_id') keySet.add(k);
      });
    });

    const textColumns: Array<{ key: 'teacher_join_date' | 'highest_level_taught'; label: string }> = [];
    if (keySet.has('teacher_join_date')) {
      textColumns.push({ key: 'teacher_join_date', label: 'Teacher Join Date' });
    }
    if (keySet.has('highest_level_taught')) {
      textColumns.push({ key: 'highest_level_taught', label: 'Highest Level Taught' });
    }

    const metrics = Array.from(keySet).filter((k) => {
      if (k === 'teacher_name' || k === 'payout_category' || k === 'course_category' || k === 'teacher_join_date' || k === 'highest_level_taught' || k === 'current_working_status' || k === 'employment_status' || k === 'teacher_leader_name') return false;
      const sample = rows.find((r) => r?.[k] !== undefined)?.[k];
      return sample && typeof sample === 'object' && !Array.isArray(sample) && 'value' in sample;
    });

    const standalone: string[] = [];
    const bucketsMap: Record<string, string[]> = {};

    metrics.forEach((metricKey) => {
      const sample = rows.find((r) => r?.[metricKey] !== undefined)?.[metricKey] as MetricCell;
      const bucket = sample?.bucket;
      if (bucket) {
        if (!bucketsMap[bucket]) bucketsMap[bucket] = [];
        bucketsMap[bucket].push(metricKey);
      } else {
        standalone.push(metricKey);
      }
    });

    const bucketOrder = Object.keys(bucketsMap).sort();
    const orderedBuckets = bucketOrder.map((bucketName) => ({
      bucketName,
      keys: bucketsMap[bucketName],
    }));

    return [
      { type: 'teacher' as const, key: 'teacher_name', label: 'Teacher', bucket: null },
      { type: 'teacher_id' as const, key: 'teacher_id', label: 'Teacher ID', bucket: null },
      { type: 'text' as const, key: 'teacher_leader_name', label: 'Teacher Leader', bucket: null },
      { type: 'working_status' as const, key: 'current_working_status', label: 'Working Status', bucket: null },
      { type: 'payout' as const, key: 'payout_category', label: 'Payout Category', bucket: null },
      { type: 'course_cat' as const, key: 'course_category', label: 'Course Category', bucket: null },
      ...textColumns.map((col) => ({
        type: 'text' as const,
        key: col.key,
        label: col.label,
        bucket: null,
      })),
      ...standalone.map((k) => {
        const sample = rows.find((r) => r?.[k] !== undefined)?.[k] as MetricCell;
        return {
          type: 'metric' as const,
          key: k,
          label: sample?.heading || k,
          bucket: null,
        };
      }),
      ...orderedBuckets.flatMap((b) =>
        b.keys.map((k) => {
          const sample = rows.find((r) => r?.[k] !== undefined)?.[k] as MetricCell;
          return {
            type: 'metric' as const,
            key: k,
            label: sample?.heading || k,
            bucket: b.bucketName,
          };
        }),
      ),
    ];
  }, [rows]);

  const bucketGroups = useMemo(() => {
    const map: Record<string, number> = {};
    tableSchema.forEach((col) => {
      if (col.bucket) {
        map[col.bucket] = (map[col.bucket] || 0) + 1;
      }
    });
    return Object.entries(map).map(([title, count]) => ({ title, count }));
  }, [tableSchema]);

  const getCellSortValue = (row: ReportRow, colKey: string): string | number => {
    if (colKey === 'teacher_name') return (row.teacher_name || '').toLowerCase();
    if (colKey === 'teacher_id') {
      const id = row.teacher_id;
      return id === null || id === undefined ? '' : Number(id);
    }
    if (colKey === 'current_working_status') {
      return String(row.current_working_status || '').toLowerCase();
    }
    if (colKey === 'payout_category') return (row.payout_category || '').toLowerCase();
    if (colKey === 'course_category') return (row.course_category || '').toLowerCase();
    if (colKey === 'teacher_join_date') return row.teacher_join_date || '';
    if (colKey === 'highest_level_taught') return (row.highest_level_taught || '').toLowerCase();
    if (colKey === 'teacher_leader_name') return (row.teacher_leader_name || '').toLowerCase();
    const m = row?.[colKey] as MetricCell | undefined;
    const v = m?.value;
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    const n = Number(String(v).replace(/,/g, ''));
    if (!Number.isNaN(n)) return n;
    return String(v).toLowerCase();
  };

  const compareRows = (a: ReportRow, b: ReportRow, key: string, dir: SortDir): number => {
    const va = getCellSortValue(a, key);
    const vb = getCellSortValue(b, key);
    let cmp = 0;
    if (typeof va === 'number' && typeof vb === 'number') {
      cmp = va - vb;
    } else {
      cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    }
    return dir === 'asc' ? cmp : -cmp;
  };

  const metricKeys = useMemo(() => {
    const findSchemaMetric = (...matchers: RegExp[]) =>
      tableSchema.find(
        (col) => col.type === 'metric' && matchers.every((re) => re.test(col.label))
      )?.key ?? null;

    const d14FromSchema = findSchemaMetric(/d14/i, /engaged/i, /%|percent/i);

    return {
      overall:
        findSchemaMetric(/^overall\s*score$/i) ||
        findMetricKey(rows, [/overall_score/i, /^overall$/i]),
      qaLd:
        findSchemaMetric(/qa/i, /l\s*&?\s*d/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/qa.*l\s*&?\s*d/i, /qa_score_ld/i, /ld_component/i],
          [/qa\s*score\s*[-–]\s*l\s*&?\s*d/i, /l\s*&?\s*d\s*component/i]
        ),
      qaAcademic:
        findSchemaMetric(/qa/i, /academic/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/qa.*academic/i, /qa_score_academic/i, /academic_component/i],
          [/qa\s*score\s*[-–]\s*academic/i, /academic\s*component/i]
        ),
      d14: d14FromSchema || findD14EngagedPercentKey(rows),
      joinedLate:
        findSchemaMetric(/^joined\s*late$/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/^joined_late$/i, /joined_late/i],
          [/^joined\s*late$/i]
        ),
      refunds:
        findSchemaMetric(/^refunds?$/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/^refunds?$/i, /refund_count/i],
          [/^refunds?$/i]
        ),
      pipActive:
        findSchemaMetric(/pip/i, /active/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/pip_active/i, /^pip$/i],
          [/pip\s*active/i]
        ),
      escalations:
        findSchemaMetric(/valid/i, /escalation/i) ||
        findSchemaMetric(/^escalations?$/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/valid_escalation/i, /escalation/i],
          [/valid\s*escalation/i, /^escalations?$/i, /total\s*escalation/i]
        ),
      engagementScore:
        findSchemaMetric(/^engagement\s*score$/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/^engagement_score$/i, /engagement_score/i],
          [/^engagement\s*score$/i]
        ),
      academicsScore:
        findSchemaMetric(/^academics?\s*score$/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/^academics?_score$/i, /academics_score/i],
          [/^academics?\s*score$/i]
        ),
      disciplineScore:
        findSchemaMetric(/discipline/i, /ops/i, /score/i) ||
        findMetricKeyByKeyOrHeading(
          rows,
          [/discipline.*ops.*score/i, /discipline_ops_score/i],
          [/discipline\s*ops\s*score/i, /discipline\s*&?\s*ops\s*score/i]
        ),
    };
  }, [rows, tableSchema]);

  const filterOptions = useMemo(
    () => ({
      courseCategories: uniqueSorted(rows.map((row) => row.course_category)),
      payoutTiers: uniqueSorted(rows.map((row) => row.payout_category)),
      workingStatuses: uniqueSorted(rows.map((row) => row.current_working_status)),
      levels: uniqueSorted(rows.map((row) => row.highest_level_taught)),
      employmentStatuses: uniqueSorted(rows.map((row) => row.employment_status)),
    }),
    [rows]
  );

  const selectedAlertsOption = useMemo(
    () =>
      ALERT_FILTER_OPTIONS.find((opt) => opt.value === alertsFilter) ||
      ALERT_FILTER_OPTIONS[0],
    [alertsFilter]
  );

  const filteredRows = useMemo(() => {
    const q = searchTeacher.trim().toLowerCase();
    const d14AsRatio = isD14ScaleRatio(rows, metricKeys.d14);
    const alertKeys: AlertMetricKeys = {
      qaLd: metricKeys.qaLd,
      qaAcademic: metricKeys.qaAcademic,
      d14: metricKeys.d14,
      joinedLate: metricKeys.joinedLate,
      refunds: metricKeys.refunds,
      pipActive: metricKeys.pipActive,
      escalations: metricKeys.escalations,
    };
    return rows.filter((row) => {
      if (q && !(row.teacher_name || '').toLowerCase().includes(q)) return false;
      if (courseCategoryFilter && row.course_category !== courseCategoryFilter) return false;
      if (payoutTierFilter && row.payout_category !== payoutTierFilter) return false;
      if (workingStatusFilter && row.current_working_status !== workingStatusFilter) return false;
      if (levelFilter && row.highest_level_taught !== levelFilter) return false;
      if (employmentStatusFilter && row.employment_status !== employmentStatusFilter) return false;
      if (!matchesAlertsFilter(row, alertsFilter, alertKeys, d14AsRatio)) return false;
      return true;
    });
  }, [
    rows,
    searchTeacher,
    courseCategoryFilter,
    payoutTierFilter,
    workingStatusFilter,
    levelFilter,
    employmentStatusFilter,
    alertsFilter,
    metricKeys.qaLd,
    metricKeys.qaAcademic,
    metricKeys.d14,
    metricKeys.joinedLate,
    metricKeys.refunds,
    metricKeys.pipActive,
    metricKeys.escalations,
  ]);

  const summaryStats = useMemo(() => {
    return {
      totalTeachers: filteredRows.length,
      avgQaLd: averageMetric(filteredRows, metricKeys.qaLd),
      avgQaAcademic: averageMetric(filteredRows, metricKeys.qaAcademic),
      d14Low: countD14Below60(filteredRows, metricKeys.d14),
      avgOverall: averageMetric(filteredRows, metricKeys.overall),
      avgEngagement: averageMetric(filteredRows, metricKeys.engagementScore),
      avgAcademics: averageMetric(filteredRows, metricKeys.academicsScore),
      avgDiscipline: averageMetric(filteredRows, metricKeys.disciplineScore),
    };
  }, [
    filteredRows,
    metricKeys.overall,
    metricKeys.qaLd,
    metricKeys.qaAcademic,
    metricKeys.d14,
    metricKeys.engagementScore,
    metricKeys.academicsScore,
    metricKeys.disciplineScore,
  ]);

  const displayRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const next = [...filteredRows];
    next.sort((a, b) => compareRows(a, b, sortKey, sortDir));
    return next;
  }, [filteredRows, sortKey, sortDir]);

  const handleSortClick = (colKey: string) => {
    if (sortKey === colKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(colKey);
      setSortDir('asc');
    }
  };

  /**
   * The report payload only carries teacher_id, but the profile route needs
   * credentialId too, and the profile API nests it inconsistently — so scan the
   * response graph for any credential-ish id before falling back to teacher search.
   */
  const findCredentialIdDeep = (payload: any): string | null => {
    const queue: any[] = [payload];
    const seen = new Set<any>();

    while (queue.length) {
      const node = queue.shift();
      if (!node || typeof node !== 'object' || seen.has(node)) continue;
      seen.add(node);

      for (const [key, value] of Object.entries(node)) {
        const isCredentialKey = /credential/i.test(key);
        if (isCredentialKey) {
          if (typeof value === 'number' || typeof value === 'string') {
            const normalized = String(value).trim();
            if (normalized && normalized !== 'null' && normalized !== 'undefined') {
              return normalized;
            }
          }
          if (value && typeof value === 'object') {
            const nestedId = (value as any).id ?? (value as any).credentialId;
            if (nestedId != null && String(nestedId).trim() !== '') {
              return String(nestedId).trim();
            }
          }
        }
        if (value && typeof value === 'object') queue.push(value);
      }
    }
    return null;
  };

  const resolveTeacherCredentialId = async (row: ReportRow): Promise<string | null> => {
    const fromRow = findCredentialIdDeep(row);
    if (fromRow) return fromRow;
    if (row.teacher_id == null) return null;

    const profile = await teacherProfile(row.teacher_id);
    const fromProfile = findCredentialIdDeep(profile);
    if (fromProfile) return fromProfile;

    // Last resort: the teacher search list always returns credentialId.
    const details = profile?.teacherProfileDetails ?? {};
    const searchPayloads = [
      details.emailId ? { emailId: String(details.emailId).trim() } : null,
      details.mobileNumber ? { mobileNumber: String(details.mobileNumber).trim() } : null,
    ].filter(Boolean) as Array<{ emailId?: string; mobileNumber?: string }>;

    for (const payload of searchPayloads) {
      const matches = await getTeachers(0, 20, payload);
      const match =
        matches.find((teacher: any) => String(teacher?.teacherId) === String(row.teacher_id)) ||
        (matches.length === 1 ? matches[0] : null);
      if (match?.credentialId != null && String(match.credentialId).trim() !== '') {
        return String(match.credentialId).trim();
      }
    }

    console.warn('No credentialId found for teacher', row.teacher_id, profile);
    return null;
  };

  const openTeacherProfile = async (row: ReportRow) => {
    if (row.teacher_id == null) {
      toast.error('Teacher ID missing — cannot open profile');
      return;
    }
    try {
      const credentialId = await resolveTeacherCredentialId(row);
      if (!credentialId) {
        toast.error(`Could not find credential ID for teacher ${row.teacher_id}`);
        return;
      }
      const base = process.env.PUBLIC_URL || '';
      window.open(
        `${base}/pages/teacher-profile/${credentialId}/${row.teacher_id}`,
        '_blank',
        'noopener,noreferrer'
      );
    } catch (error: any) {
      console.error('Error opening teacher profile', error);
      toast.error(
        error?.response?.data?.message ||
          `Failed to load profile for teacher ${row.teacher_id}`
      );
    }
  };

  const openMetricTrend = async (row: ReportRow, column: SchemaCol) => {
    if (column.type !== 'metric' || row.teacher_id === null || row.teacher_id === undefined) {
      return;
    }
    const numericValue = getMetricValue(row, column.key);
    if (numericValue === null) return;

    const normalizedLabel = normalizeHeading(column.label);
    setTrendContext({
      heading: column.label,
      teacherName: row.teacher_name || 'Teacher',
      courseCategory: row.course_category ? String(row.course_category) : '',
      showPercent:
        normalizedLabel.includes('%') ||
        /\b(percent|percentage|pct)\b/i.test(`${column.key} ${column.label}`),
    });
    setTrendData(null);
    setTrendError('');
    setTrendModalOpen(true);
    setTrendLoading(true);

    try {
      const response = await getTeacherMetricPastMonthsData(row.teacher_id, column.key);
      const payload = response?.data && !response?.past_months_data ? response.data : response;
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid trend response');
      }
      setTrendData(payload as TeacherMetricTrend);
    } catch (error: any) {
      console.error('Error loading metric trend', error);
      setTrendError(error?.response?.data?.message || 'Failed to load the six-month trend.');
    } finally {
      setTrendLoading(false);
    }
  };

  const sortIndicator = (colKey: string) => {
    if (sortKey !== colKey) return ' ⇅';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const handleFetch = async () => {
    if (!month) {
      toast.error('Please select year/month');
      return;
    }

    setLoading(true);
    try {
      const year = month.split('-')?.[0] || String(new Date().getFullYear());
      const quarterStart =
        `${year}-${quarterFilter === 'Q1' ? '01' : quarterFilter === 'Q2' ? '04' : quarterFilter === 'Q3' ? '07' : '10'}`;
      const quarterEnd =
        `${year}-${quarterFilter === 'Q1' ? '03' : quarterFilter === 'Q2' ? '06' : quarterFilter === 'Q3' ? '09' : '12'}`;
      let response: any;
      if (teacherLeaderView) {
        const teacherLeaderUserId = getTeacherLeaderUserId();
        if (!teacherLeaderUserId) {
          toast.error('Teacher leader user id is missing.');
          setRawResponse(null);
          setRows([]);
          return;
        }
        response = await getReportsForTeacherLeaderForAdmin(
          teacherLeaderUserId,
          quarterFilter === 'MONTHLY'
            ? { month }
            : { startMonth: quarterStart, endMonth: quarterEnd }
        );
      } else if (quarterFilter === 'MONTHLY') {
        response = await getReportsForAdmin(month);
      } else {
        response = await getReportsForAdminRange(quarterStart, quarterEnd);
      }
      setRawResponse(response);
      const normalized = normalizeRows(response);
      setRows(normalized);
      setSortKey(null);
      setAlertsMenuOpen(false);

      // Keep the user's filters across a refetch; only drop dropdown values
      // that no longer exist in the newly loaded dataset.
      const keepIfPresent = (
        current: string,
        pick: (row: ReportRow) => any
      ): string =>
        !current || normalized.some((row) => String(pick(row) ?? '') === current)
          ? current
          : '';

      setCourseCategoryFilter((current) =>
        keepIfPresent(current, (row) => row.course_category)
      );
      setPayoutTierFilter((current) =>
        keepIfPresent(current, (row) => row.payout_category)
      );
      setWorkingStatusFilter((current) =>
        keepIfPresent(current, (row) => row.current_working_status)
      );
      setLevelFilter((current) =>
        keepIfPresent(current, (row) => row.highest_level_taught)
      );
      setEmploymentStatusFilter((current) =>
        keepIfPresent(current, (row) => row.employment_status)
      );
    } catch (error: any) {
      console.error('Error fetching admin reports', error);
      toast.error(error?.response?.data?.message || 'Failed to fetch reports');
      setRawResponse(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  /** RFC 4180-style cell: always quoted so Excel / Sheets split columns reliably. */
  const toCsvCell = (val: string | number | null | undefined): string => {
    const str = val === null || val === undefined ? '' : String(val);
    return `"${str.replace(/"/g, '""')}"`;
  };

  const downloadCsv = () => {
    if (!tableSchema.length || !displayRows.length) {
      toast.warning('No data to download');
      return;
    }
    const headerLine = tableSchema.map((c) => toCsvCell(c.label)).join(',');
    const lines = [headerLine];
    displayRows.forEach((row) => {
      const cells = tableSchema.map((col) => {
        if (col.type === 'teacher') return toCsvCell(row.teacher_name ?? '');
        if (col.type === 'teacher_id') return toCsvCell(row.teacher_id ?? '');
        if (col.type === 'working_status') return toCsvCell(row.current_working_status ?? '');
        if (col.type === 'payout') return toCsvCell(row.payout_category ?? '');
        if (col.type === 'course_cat') return toCsvCell(row.course_category ?? '');
        if (col.type === 'text') return toCsvCell(row[col.key] ?? '');
        const m = row[col.key] as MetricCell | undefined;
        return toCsvCell(m?.value ?? '');
      });
      lines.push(cells.join(','));
    });
    // UTF-8 BOM + CRLF: helps Excel (especially Windows) and Google Sheets auto-detect columns
    const csvText = `\uFEFF${lines.join('\r\n')}`;
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const year = month.split('-')?.[0] || String(new Date().getFullYear());
    const rangeLabel =
      quarterFilter === 'MONTHLY'
        ? month
        : `${year}-${quarterFilter}`;
    a.download = `teacher-report-${rangeLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };


  /** Backend `colour` → text tone class for a metric value. */
  const getValueToneClass = (colour?: string) => {
    switch ((colour || '').toLowerCase()) {
      case 'green':
        return 'atr-tone--success';
      case 'red':
        return 'atr-tone--danger';
      case 'amber':
      case 'orange':
      case 'yellow':
        return 'atr-tone--warning';
      case 'blue':
        return 'atr-tone--info';
      case 'grey':
      case 'gray':
        return 'atr-tone--muted';
      default:
        return 'atr-tone--default';
    }
  };

  const getPayoutBadge = (category: string): { tone: BadgeTone; emoji: string } => {
    const normalized = category.toLowerCase();
    if (normalized === 'gold') return { tone: 'warning', emoji: '🥇' };
    if (normalized === 'silver') return { tone: 'neutral', emoji: '🥈' };
    if (normalized === 'platinum') return { tone: 'primary', emoji: '👑' };
    if (normalized === 'diamond') return { tone: 'accent', emoji: '💎' };
    return { tone: 'neutral', emoji: '🏅' };
  };

  const topHeaderColumns = tableSchema.filter((col) => !col.bucket);

  const ariaSort = (colKey: string): React.AriaAttributes['aria-sort'] =>
    sortKey !== colKey ? undefined : sortDir === 'asc' ? 'ascending' : 'descending';

  const sortButton = (column: SchemaCol) => (
    <button
      type="button"
      className="atr-sort"
      title="Click to sort"
      onClick={() => handleSortClick(column.key)}
    >
      {column.label}
      {sortIndicator(column.key)}
    </button>
  );

  const optionsWithAll = (values: string[]) => [
    { value: '', label: 'All' },
    ...values.map((value) => ({ value, label: value })),
  ];

  const periodOptions = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'Q1', label: 'Q1 (Jan - Mar)' },
    { value: 'Q2', label: 'Q2 (Apr - Jun)' },
    { value: 'Q3', label: 'Q3 (Jul - Sep)' },
    { value: 'Q4', label: 'Q4 (Oct - Dec)' },
  ];

  const trendPercent = Boolean(trendContext?.showPercent);
  const lowIsGood = String(trendData?.parameter_direction).toLowerCase() === 'low';

  return (
    <Page className="admin-teacher-reports">
      <PageHeader
        eyebrow="Reports"
        title="Teacher Reports"
        subtitle="Monthly and quarterly teacher scorecards. Click a score to see its six-month trend."
        actions={
          <Button
            icon={<Download size={16} aria-hidden />}
            onClick={downloadCsv}
            disabled={loading || !displayRows.length}
          >
            CSV
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <Field label="Search" className="ds-toolbar__grow">
            {(ctl) => (
              <SearchInput
                {...ctl}
                size="sm"
                value={searchTeacher}
                onChange={(e) => setSearchTeacher(e.target.value)}
                placeholder="Teacher name..."
              />
            )}
          </Field>

          <Field label="Course Category">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                options={optionsWithAll(filterOptions.courseCategories)}
                value={courseCategoryFilter}
                onChange={(e) => setCourseCategoryFilter(e.target.value)}
              />
            )}
          </Field>

          <Field label="Payout Tier">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                options={optionsWithAll(filterOptions.payoutTiers)}
                value={payoutTierFilter}
                onChange={(e) => setPayoutTierFilter(e.target.value)}
              />
            )}
          </Field>

          <Field label="Working Status">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                options={optionsWithAll(filterOptions.workingStatuses)}
                value={workingStatusFilter}
                onChange={(e) => setWorkingStatusFilter(e.target.value)}
              />
            )}
          </Field>

          <Field label="Level">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                options={optionsWithAll(filterOptions.levels)}
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              />
            )}
          </Field>

          {/* Employment Status filter intentionally hidden (state kept: filterOptions.employmentStatuses). */}

          <Field label="Alerts" className="atr-alerts-field">
            {(ctl) => (
              <div ref={alertsMenuRef}>
                <button
                  id={ctl.id}
                  type="button"
                  className={`atr-alerts-trigger${alertsMenuOpen ? ' is-open' : ''}`}
                  onClick={() => setAlertsMenuOpen((open) => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={alertsMenuOpen}
                >
                  <span className="atr-alerts-trigger__icon" aria-hidden>{selectedAlertsOption.icon}</span>
                  <span className="atr-alerts-trigger__label">{selectedAlertsOption.label}</span>
                  <span className="atr-alerts-trigger__caret" aria-hidden>▾</span>
                </button>
                {alertsMenuOpen
                  ? createPortal(
                      <div
                        ref={alertsPopupRef}
                        className="atr-alerts-menu"
                        role="listbox"
                        // Anchored to the trigger's measured rect (menu is portaled to body).
                        style={{
                          top: alertsMenuPos.top,
                          left: alertsMenuPos.left,
                          minWidth: Math.max(alertsMenuPos.width, 240),
                        }}
                      >
                        {ALERT_FILTER_OPTIONS.map((opt) => {
                          const active = opt.value === alertsFilter;
                          return (
                            <button
                              key={opt.value || 'none'}
                              type="button"
                              role="option"
                              aria-selected={active}
                              className={`atr-alerts-option${active ? ' is-active' : ''}`}
                              onClick={() => {
                                setAlertsFilter(opt.value);
                                setAlertsMenuOpen(false);
                              }}
                            >
                              <span className="atr-alerts-option__icon" aria-hidden>{opt.icon}</span>
                              <span className="atr-alerts-option__label">{opt.label}</span>
                              {active ? <span className="atr-alerts-option__check" aria-hidden>✓</span> : null}
                            </button>
                          );
                        })}
                      </div>,
                      document.body
                    )
                  : null}
              </div>
            )}
          </Field>

          <Field label="Month" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="month"
                size="sm"
                value={month}
                max={todayMonth}
                onChange={(e) => setMonth(e.target.value)}
              />
            )}
          </Field>

          {teacherLeaderView ? (
            <div className="ds-field atr-scope">
              <span className="ds-field__label">Scope</span>
              <Badge tone="primary">Your assigned teachers</Badge>
            </div>
          ) : null}

          <Field label="Period">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                options={periodOptions}
                value={quarterFilter}
                onChange={(e) => setQuarterFilter(e.target.value as typeof quarterFilter)}
              />
            )}
          </Field>

          <div className="ds-toolbar__end">
            <Button variant="primary" size="sm" onClick={handleFetch} loading={loading}>
              Get
            </Button>
          </div>
        </Toolbar>
      </Card>

      {loading && <Loader label="Loading teacher reports…" />}

      {!loading && rows.length === 0 && !rawResponse && (
        <EmptyState
          title="No report loaded"
          description="Pick a month and period, then press Get to load teacher scorecards."
        />
      )}

      {!loading && month && rows.length === 0 && rawResponse && (
        <EmptyState
          title={`No report rows found for ${quarterFilter === 'MONTHLY' ? 'selected month' : `quarter ${quarterFilter}`}.`}
          description="Try another month or period."
        />
      )}

      {!loading && rows.length > 0 && filteredRows.length === 0 && (
        <EmptyState
          title="No teacher matches your filters."
          description="Clear the search or loosen the category, tier, status, level or alert filters."
        />
      )}

      {!loading && displayRows.length > 0 && (
        <>
          <div className="atr-summary-row">
            <StatTile align="start" tone="primary" label="Total Teachers" value={summaryStats.totalTeachers} hint="in current view" />
            <StatTile
              align="start"
              tone="warning"
              label="Avg QA L&D"
              value={summaryStats.avgQaLd ?? '—'}
              progress={progressWidth(summaryStats.avgQaLd)}
              hint="QA Score – L&D Component"
            />
            <StatTile
              align="start"
              tone="warning"
              label="Avg QA Academic"
              value={summaryStats.avgQaAcademic ?? '—'}
              progress={progressWidth(summaryStats.avgQaAcademic)}
              hint="QA Score – Academic Component"
            />
            <StatTile align="start" tone="danger" label="D14 Engaged < 60%" value={summaryStats.d14Low} hint="below 60% threshold" />
            <StatTile
              align="start"
              tone="warning"
              label="Avg Overall Score"
              value={summaryStats.avgOverall ?? '—'}
              progress={progressWidth(summaryStats.avgOverall)}
              hint="across all teachers"
            />
            <StatTile
              align="start"
              tone="accent"
              label="Avg Engagement"
              value={summaryStats.avgEngagement ?? '—'}
              progress={progressWidth(summaryStats.avgEngagement)}
              hint="Engagement Score"
            />
            <StatTile
              align="start"
              tone="info"
              label="Avg Academics"
              value={summaryStats.avgAcademics ?? '—'}
              progress={progressWidth(summaryStats.avgAcademics)}
              hint="Academics Score"
            />
            <StatTile
              align="start"
              tone="success"
              label="Avg Discipline & Ops"
              value={summaryStats.avgDiscipline ?? '—'}
              progress={progressWidth(summaryStats.avgDiscipline)}
              hint="Discipline Ops Score"
            />
          </div>

          <Card
            flush
            className="atr-table-card"
            title="Teacher scorecards"
            subtitle={
              <>
                Showing {displayRows.length} of {rows.length} teacher(s)
                {searchTeacher.trim() ||
                courseCategoryFilter ||
                payoutTierFilter ||
                workingStatusFilter ||
                levelFilter ||
                employmentStatusFilter ||
                alertsFilter
                  ? ' (filtered)'
                  : ''}
              </>
            }
            actions={
              <div className="atr-legend">
                <span className="atr-legend__item"><span className="atr-legend__dot atr-legend__dot--success" aria-hidden />Above Average</span>
                <span className="atr-legend__item"><span className="atr-legend__dot atr-legend__dot--warning" aria-hidden />Average</span>
                <span className="atr-legend__item"><span className="atr-legend__dot atr-legend__dot--danger" aria-hidden />Below Average</span>
                <span className="atr-legend__item"><span className="atr-legend__dot atr-legend__dot--muted" aria-hidden />Not considered for Scoring</span>
              </div>
            }
          >
            <Table caption="Teacher report scorecards" className="atr-table">
              <thead>
                <tr>
                  {topHeaderColumns.map((column, index) => (
                    <th
                      key={`top-${column.key}`}
                      rowSpan={2}
                      aria-sort={ariaSort(column.key)}
                      className={[
                        index === 0 ? 'atr-sticky-col' : '',
                        column.key === 'overall_score' ? 'atr-col-strong' : '',
                      ].filter(Boolean).join(' ') || undefined}
                    >
                      {sortButton(column)}
                    </th>
                  ))}
                  {bucketGroups.map((group) => (
                    <th key={`group-${group.title}`} colSpan={group.count} className="atr-bucket-th">
                      {group.title}
                    </th>
                  ))}
                </tr>
                <tr>
                  {tableSchema.filter((col) => col.bucket).map((column) => (
                    <th key={`sub-${column.key}`} aria-sort={ariaSort(column.key)} className="atr-bucket-sub-th">
                      {sortButton(column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => (
                  <tr key={idx}>
                    {tableSchema.map((column) => {
                      if (column.type === 'teacher') {
                        const name = row.teacher_name || '-';
                        const canOpen = row.teacher_id != null && name !== '-';
                        return (
                          <td key={`${idx}-${column.key}`} className="atr-sticky-col">
                            {canOpen ? (
                              <button
                                type="button"
                                className="atr-teacher-link"
                                title="Open teacher profile in a new tab"
                                onClick={() => {
                                  void openTeacherProfile(row);
                                }}
                              >
                                {name}
                              </button>
                            ) : (
                              name
                            )}
                          </td>
                        );
                      }
                      if (column.type === 'teacher_id') {
                        return (
                          <td key={`${idx}-${column.key}`} className="ds-num">
                            {row.teacher_id ?? '-'}
                          </td>
                        );
                      }
                      if (column.type === 'working_status') {
                        return (
                          <td key={`${idx}-${column.key}`} className="atr-nowrap">
                            {row.current_working_status || '-'}
                          </td>
                        );
                      }
                      if (column.type === 'payout') {
                        const payout = row.payout_category ? String(row.payout_category) : '';
                        if (!payout) {
                          return <td key={`${idx}-${column.key}`}>-</td>;
                        }
                        const payoutUi = getPayoutBadge(payout);
                        return (
                          <td key={`${idx}-${column.key}`}>
                            <Badge tone={payoutUi.tone}>
                              <span aria-hidden>{payoutUi.emoji}</span>
                              <span>{payout}</span>
                            </Badge>
                          </td>
                        );
                      }
                      if (column.type === 'course_cat') {
                        return (
                          <td key={`${idx}-${column.key}`} className="atr-nowrap">
                            {row.course_category || '-'}
                          </td>
                        );
                      }
                      if (column.type === 'text') {
                        return (
                          <td key={`${idx}-${column.key}`} className="atr-nowrap ds-strong">
                            {row[column.key] || '-'}
                          </td>
                        );
                      }
                      const metric = row?.[column.key] as MetricCell;
                      const value = metric?.value ?? '-';
                      const hasNumericValue = getMetricValue(row, column.key) !== null;
                      return (
                        <td
                          key={`${idx}-${column.key}`}
                          className={[
                            'ds-num',
                            'atr-metric-cell',
                            getValueToneClass(metric?.colour),
                            hasNumericValue ? 'atr-metric-cell--clickable' : '',
                            column.key === 'overall_score' ? 'atr-col-strong' : '',
                          ].filter(Boolean).join(' ')}
                          role={hasNumericValue ? 'button' : undefined}
                          tabIndex={hasNumericValue ? 0 : undefined}
                          title={hasNumericValue ? 'Click to view six-month trend' : undefined}
                          onClick={() => {
                            if (hasNumericValue) void openMetricTrend(row, column);
                          }}
                          onKeyDown={(event) => {
                            if (hasNumericValue && (event.key === 'Enter' || event.key === ' ')) {
                              event.preventDefault();
                              void openMetricTrend(row, column);
                            }
                          }}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      <Modal
        isOpen={trendModalOpen}
        toggle={() => setTrendModalOpen(false)}
        centered
        size="lg"
        className="ds-dialog atr-trend-dialog"
      >
        <div className="atr-trend-header">
          <div>
            <h2 className="ds-dialog__title">{trendContext?.heading || 'Metric'} — 6-month trend</h2>
            <p className="ds-text-subtle atr-trend-header__sub">
              {trendData?.teacher_name || trendContext?.teacherName || 'Teacher'}
              {trendContext?.courseCategory ? ` · ${trendContext.courseCategory}` : ''}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Close trend"
            icon={<X size={18} aria-hidden />}
            onClick={() => setTrendModalOpen(false)}
          />
        </div>

        {trendLoading ? (
          <Loader label="Loading six-month trend…" />
        ) : trendError ? (
          <EmptyState title="Couldn't load the trend" description={trendError} />
        ) : trendData ? (
          <>
            <TrendChart trend={trendData} showPercent={trendPercent} />
            <div className="atr-trend-stats">
              <StatTile label="Current" value={formatTrendValue(trendData.current_value, trendPercent)} />
              <StatTile label="6-mo avg" value={formatTrendValue(trendData.six_month_average_value, trendPercent)} />
              <StatTile
                tone={lowIsGood ? 'danger' : 'success'}
                label="Highest"
                value={formatTrendValue(trendData.highest_value, trendPercent)}
              />
              <StatTile
                tone={lowIsGood ? 'success' : 'danger'}
                label="Lowest"
                value={formatTrendValue(trendData.lowest_value, trendPercent)}
              />
            </div>
            <div className="atr-trend-note ds-text-subtle">
              <span className="atr-trend-note__line" aria-hidden />
              Dashed line = 6-month average
            </div>
          </>
        ) : null}
      </Modal>
    </Page>
  );
};

export default AdminTeacherReports;
